import type { StackScope } from '@hooks/useStackableCases';
import { useStackedCasesSummary } from '@hooks/useStackableCases';
import { ExternalLink } from 'lucide-react';
import React from 'react';

import { buildRoutePath } from '@/utils/routes';

interface CallStackSummaryProps {
  /** The case the call was placed from. */
  caseRecordId: string;
  /** The call that just ended. This is the stack id. */
  callId?: string;
  /** Org and program captured when the dialpad opened. */
  scope: StackScope | null;
}

/**
 * What the call covered, read-only. Shown after hangup, when nothing more can
 * be stacked: a stack's time marks where hermes cuts each case's recording.
 *
 * Read from the stackable-cases list, which keeps the stack's members after
 * hangup and is allowed at assigned scope, so a Case Manager sees it for their
 * own call. Read once; nothing changes after hangup.
 */
export function CallStackSummary({ caseRecordId, callId, scope }: CallStackSummaryProps) {
  const { data, isLoading } = useStackedCasesSummary(scope, caseRecordId, callId);
  if (!callId) return null;
  if (isLoading) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="stack-summary-loading">
        Loading…
      </p>
    );
  }
  const covered = (data?.cases ?? []).filter(c => c.in_this_stack);
  if (covered.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="stack-summary-empty">
        No other cases were stacked on this call.
      </p>
    );
  }
  return (
    <div className="space-y-1" data-testid="stack-summary">
      <span className="text-xs font-medium text-muted-foreground">This call covered:</span>
      <ul className="max-h-24 space-y-1 overflow-y-auto text-sm">
        {covered.map(c => (
          <li key={c.case_record_id}>
            {/* A new tab: the case opens beside the dialpad, not in place of it. */}
            <a
              href={buildRoutePath(
                `/cases/${c.case_record_id ?? ''}`,
                scope?.organizationId ?? '',
                c.program_id || scope?.programId || '',
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {c.case_code || c.case_record_id}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
